<template>
  <DSDivider
    :class="DS_FILE_UPLOAD_CLASSES.Divider"
    data-cy="ds-file-upload__divider"
  />

  <DSFileItem
    :file-name="props.item.name"
    :file-type="fileType"
    :file-size="fileSize"
    :error-messages="props.item.errorMessages"
    :show-progress-bar="props.item.progress !== undefined"
    :progress="props.item.progress"
    :disabled="props.disabled"
    :author="props.item.author"
    :file-uploaded-date="props.item.fileUploadedDate"
    :is-clickable="props.item.isClickable"
    :thumbnail-src="previewUrl ?? props.item.thumbnailSrc"
    thumbnail
  >
    <template #actions>
      <DSButton
        variant="ghost"
        size="sm"
        type="button"
        prefix-icon="trash"
        :aria-label="removeFileLabel(props.item.name)"
        :disabled="props.disabled"
        data-cy="ds-file-upload__remove-button"
        @click.stop="emit('remove', props.item)"
      />
    </template>
  </DSFileItem>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import DSFileItem from "@/src/design_system/data_display/file_item/components/DSFileItem.vue";
import { DS_IMAGE_FILE_PREVIEW_EXTENSIONS } from "@/src/design_system/shared/constants/fileTypeIcons";
import { formatFileSize } from "@/src/shared/utils/filesize";
import { DS_FILE_UPLOAD_CLASSES } from "../constants/classes";
import type { DSFileUploadItem } from "../types";
import DSDivider from "../../../layouts_and_containers/divider/components/DSDivider.vue";

interface DSFileUploadFileItemProps {
  item: DSFileUploadItem;
  disabled: boolean;
}

interface DSFileUploadFileItemEmits {
  (e: "remove", item: DSFileUploadItem): void;
}

const props = defineProps<DSFileUploadFileItemProps>();
const emit = defineEmits<DSFileUploadFileItemEmits>();
const previewUrl = ref<string>();
const fileSize = computed(() => (props.item.size === undefined ? undefined : formatFileSize(String(props.item.size))));
const fileType = computed(() => {
  const extensionIndex = props.item.name.lastIndexOf(".");

  if (extensionIndex <= 0 || extensionIndex === props.item.name.length - 1) return undefined;

  return props.item.name.slice(extensionIndex + 1).toUpperCase();
});

const isPreviewableFile = (fileName: string): boolean => {
  const lowerFileName = fileName.toLowerCase();

  return DS_IMAGE_FILE_PREVIEW_EXTENSIONS.some(extension => lowerFileName.endsWith(`.${extension}`));
};

const removeFileLabel = (fileName: string): string => {
  return window.I18n.designSystem.fileUpload.removeFile.replace("%{file_name}", () => fileName);
};

watch(
  () => [props.item.file, props.item.name] as const,
  ([file, fileName], _, onCleanup) => {
    previewUrl.value = undefined;

    if (!file || !isPreviewableFile(fileName)) return;

    const objectUrl = URL.createObjectURL(file);
    previewUrl.value = objectUrl;

    onCleanup(() => URL.revokeObjectURL(objectUrl));
  },
  { immediate: true }
);
</script>
